import { useQuery } from '@tanstack/react-query';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { PackDownloadResult, listPackDownloads } from './api';
import { formatAt } from './format';

const POLL_MS = 60_000;

const RESULT_CHIP: Record<PackDownloadResult, { label: string; color: 'success' | 'error' | 'warning' | 'default' }> = {
  sent: { label: 'sent', color: 'success' },
  license_invalid: { label: 'licence invalid', color: 'error' },
  not_licensed: { label: 'not licensed', color: 'warning' },
  not_published: { label: 'not published', color: 'default' },
};

/**
 * "Pack downloads": what the MES pack service handed out (or refused), newest
 * first — one row per POST /api/mes-packs/download (MES 5.1 add-on packs,
 * section 8). Its own dashboard tab since the licence registry left the
 * tunnel (2026-10-23). licenceId and customer are what the licence claimed;
 * for "licence invalid" rows they are unverified.
 */
export function PackDownloadsTable() {
  const downloads = useQuery({
    queryKey: ['mes-pack-downloads'],
    queryFn: () => listPackDownloads(),
    refetchInterval: POLL_MS,
  });
  const rows = downloads.data ?? [];

  return (
    <Box sx={{ display: 'grid', gap: 1 }} data-testid="pack-downloads">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="h6" component="h2">
          Pack downloads
        </Typography>
        <Tooltip title="Refresh">
          <span>
            <IconButton
              size="small"
              aria-label="refresh pack downloads"
              disabled={downloads.isFetching}
              onClick={() => void downloads.refetch()}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      {downloads.isLoading ? (
        <Box sx={{ display: 'grid', placeItems: 'center', p: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : downloads.isError ? (
        <Alert severity="error">
          {downloads.error instanceof Error ? downloads.error.message : 'could not load pack downloads'}
        </Alert>
      ) : rows.length === 0 ? (
        <Typography color="text.secondary" sx={{ p: 2 }}>
          No MES add-on pack downloads yet.
        </Typography>
      ) : (
        <Paper elevation={1}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Time</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Licence</TableCell>
                  <TableCell>Add-on</TableCell>
                  <TableCell>MES release</TableCell>
                  <TableCell>Result</TableCell>
                  <TableCell>Address</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => {
                  const chip = RESULT_CHIP[r.result] ?? { label: r.result, color: 'default' as const };
                  return (
                    <TableRow key={r.id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatAt(r.at)}</TableCell>
                      <TableCell>{r.customer ?? '—'}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{r.licenseId ?? '—'}</TableCell>
                      <TableCell>{r.addonId}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                        {r.platformVersion} ({r.platformBuildId})
                      </TableCell>
                      <TableCell>
                        <Chip label={chip.label} color={chip.color} size="small" variant={r.result === 'sent' ? 'filled' : 'outlined'} />
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{r.remoteAddress ?? '—'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
}
